import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MenuItem } from '../../../../core/models/menu-item';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter, map, startWith } from 'rxjs';
import {
  getActiveMenuItem,
  handlePluralTranslation,
} from '../../../../core/utils';
import { User } from '../../../../core/models/user';
import { UserRole } from '../../../../core/enums';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { GlobalLoadingBarService } from '../../../../core/services/global-loading-bar.service';
import { AuthService } from '../../../../core/services/api/auth.service';
import { FeathersService } from '../../../../core/services/api/feathers.service';
import { ScreenSizeService } from '../../../../core/services/screen-size.service';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'ling-header',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    RouterModule,
    MatProgressBarModule,
    TranslateModule,
  ],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent implements OnInit {
  @ViewChild('mobileHeader') mobileHeaderRef!: ElementRef;
  @ViewChild('mobileDropdown') mobileDropdownRef!: ElementRef;
  @ViewChild('desktopSettingsRoot') desktopSettingsRootRef?: ElementRef;

  @Input() isPreviewPage = false;

  handlePluralTranslation = handlePluralTranslation;
  user?: User;
  credits? = 0;
  headerMenuItems: MenuItem[] = [
    {
      text: 'header.createCertificate',
      linkRoute: 'certificates/create',
      associatedRoutes: ['certificates/create', 'certificates/edit'],
    },
    // {
    //   text: 'header.createEdition',
    //   linkRoute: 'certificates/edition',
    //   associatedRoutes: ['certificates/edition'],
    // },
    {
      text: 'header.myCertificates',
      linkRoute: 'certificates',
      associatedRoutes: ['certificates'],
    },
    {
      text: 'header.mediaLibrary',
      linkRoute: 'media-library',
      associatedRoutes: ['media-library'],
    },
    {
      text: 'header.settings',
      linkRoute: 'profile',
      associatedRoutes: ['profile'],
      icon: 'assets/img/gear.svg',
    },
  ];

  isMobileHeader = false;
  mobileDropdownOpened = false;
  desktopSettingsDropdownOpened = false;

  desktopSettingsTabs: MenuItem[] = [];
  private closeDesktopSettingsDropdownTimeoutId?: number;

  private readonly commonProfileTabs: MenuItem[] = [
    {
      text: 'settingsPage.profile',
      linkRoute: '/profile/info',
      associatedRoutes: ['/profile/info'],
    },
    {
      text: 'settingsPage.certificateConfiguration',
      linkRoute: '/profile/certificate-configuration',
      associatedRoutes: ['/profile/certificate-configuration'],
    },
    {
      text: 'settingsPage.notifications',
      linkRoute: '/profile/notifications',
      associatedRoutes: ['/profile/notifications'],
    },
    {
      text: 'settingsPage.credits',
      linkRoute: '/profile/credits',
      associatedRoutes: ['/profile/credits'],
    },
  ];

  private readonly adminProfileTabs: MenuItem[] = [
    {
      text: 'settingsPage.administration',
      linkRoute: '/profile/admin',
      associatedRoutes: ['/profile/admin'],
    },
    {
      text: 'settingsPage.users',
      linkRoute: '/profile/users',
      associatedRoutes: ['/profile/users'],
    },
    {
      text: 'settingsPage.whitelist',
      linkRoute: '/profile/whitelist',
      associatedRoutes: ['/profile/whitelist'],
    },
  ];

  private getDesktopSettingsTabsByRole(user: User | undefined): MenuItem[] {
    if (!user) {
      return [];
    }

    return user?.permissions === UserRole.Admin
      ? [...this.commonProfileTabs, ...this.adminProfileTabs]
      : [...this.commonProfileTabs];
  }
  getDesktopSettingsTabsForRender(): MenuItem[] {
    return this.getDesktopSettingsTabsByRole(this.user);
  }

  isOnLoginPage$ = this.router.events.pipe(
    filter((e) => e instanceof NavigationEnd),
    map(() => this.router.url),
    startWith(this.router.url),
    map((route) => route.split('?')[0]),
    filter((route) => !!route.startsWith('/login'))
  );
  nextRouteAfterLogin$ = this.router.events.pipe(
    filter((e) => e instanceof NavigationEnd),
    map(() => this.router.url),
    startWith(this.router.url),
    map((route) => route.split('?')[0]),
    filter((route) => route !== '/login' && route !== '/')
  );

  @HostListener('document:click', ['$event.target'])
  onDocumentClick(target: any) {
    if (!this.isMobileHeader) {
      if (!this.desktopSettingsRootRef?.nativeElement?.contains(target)) {
        this.closeDesktopSettingsDropdown();
      }
      return;
    }
    if (
      !(
        this.mobileHeaderRef.nativeElement.contains(target) ||
        this.mobileDropdownRef.nativeElement.contains(target)
      )
    ) {
      this.toggleDropdown(false);
    }
  }

  constructor(
    private screenSize: ScreenSizeService,
    public router: Router,
    private cd: ChangeDetectorRef,
    public loader: GlobalLoadingBarService,
    private auth: AuthService,
    private feathers: FeathersService
  ) {}

  ngOnInit(): void {
    this.screenSize.maxWidth(851).subscribe((state) => {
      this.isMobileHeader = state;
      const settingsItem = this.headerMenuItems.find(
        (item) => item.linkRoute === 'profile'
      );
      if (this.isMobileHeader) {
        settingsItem!.icon = '';
      } else {
        settingsItem!.icon = 'assets/img/gear.svg';
      }
      this.cd.detectChanges();
    });
    this.feathers.me$.subscribe((user) => {
      this.user = user;
      this.credits = user?.credits ?? 0;
      this.desktopSettingsTabs = this.getDesktopSettingsTabsByRole(user);
      this.cd.markForCheck();
    });

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.closeDesktopSettingsDropdown();
        this.cd.markForCheck();
      });
  }

  isMenuItemActive(menuItem: MenuItem): boolean {
    return menuItem === getActiveMenuItem(this.headerMenuItems, this.router);
  }

  toggleDropdown(state?: boolean): void {
    this.mobileDropdownOpened = state ?? !this.mobileDropdownOpened;
    if (this.mobileDropdownOpened) {
      document.querySelector('html')?.classList.add('ling-global-scrollblock');
    } else {
      document
        .querySelector('html')
        ?.classList.remove('ling-global-scrollblock');
    }
  }

  openDesktopSettingsDropdown(): void {
    if (!this.user) {
      return;
    }
    window.clearTimeout(this.closeDesktopSettingsDropdownTimeoutId);
    this.desktopSettingsTabs = this.getDesktopSettingsTabsByRole(this.user);
    this.desktopSettingsDropdownOpened = true;
    this.cd.markForCheck();
  }

  toggleDesktopSettingsDropdown(): void {
    if (this.desktopSettingsDropdownOpened) {
      this.closeDesktopSettingsDropdown();
      return;
    }
    this.openDesktopSettingsDropdown();
  }

  cancelDesktopSettingsDropdownClose(): void {
    window.clearTimeout(this.closeDesktopSettingsDropdownTimeoutId);
  }

  scheduleDesktopSettingsDropdownClose(): void {
    window.clearTimeout(this.closeDesktopSettingsDropdownTimeoutId);
    this.closeDesktopSettingsDropdownTimeoutId = window.setTimeout(() => {
      this.desktopSettingsDropdownOpened = false;
      this.cd.markForCheck();
    }, 150);
  }

  closeDesktopSettingsDropdown(): void {
    window.clearTimeout(this.closeDesktopSettingsDropdownTimeoutId);
    this.desktopSettingsDropdownOpened = false;
    this.cd.markForCheck();
  }

  isDesktopProfileTabActive(tab: MenuItem): boolean {
    return tab === getActiveMenuItem(this.getDesktopSettingsTabsForRender(), this.router);
  }

  onDesktopProfileTabClick(tab: MenuItem): void {
    this.closeDesktopSettingsDropdown();
    this.router.navigate([tab.linkRoute]).then();
  }

  onLogout(): void {
    this.closeDesktopSettingsDropdown();
    this.toggleDropdown(false);
    this.auth.logout().subscribe(() => {
      this.router.navigate(['/']).then();
    });
  }
}
